import { IconChevronRight } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { BookingDetails } from "../bookingCalendarApi"
import { minutesToLabel, zonedDateAndMinutes } from "../lib/siteTime"

/**
 * The appointments competing for one row, to pick which to open.
 *
 * The grid can draw only one appointment per row, so a double-booking shows the one that won the row
 * and says there are others. This is how the others are reached: each entry opens that appointment,
 * and the page keeps this list underneath it, so closing the appointment comes back here.
 */
export function OverlapDialog({ bookings, timeZone, onPick, onClose }: {
  /** Empty or undefined renders nothing: no list is open, or an appointment from it is on top. */
  bookings: readonly BookingDetails[] | undefined
  timeZone: string
  onPick: (bookingUid: string) => void
  onClose: () => void
}) {
  if (!bookings || bookings.length === 0) return null

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Overlapping appointments</DialogTitle>
          <DialogDescription>
            {bookings.length === 1
              ? "Only one of these appointments is still booked."
              : `${bookings.length} appointments share this time. Pick one to open it.`}
          </DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col gap-1.5">
          {bookings.map(booking => (
            <li key={booking.uid}>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-primary"
                onClick={() => onPick(booking.uid)}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{attendeeNames(booking)}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {timeRange(booking, timeZone)} · {hostNames(booking)}
                  </div>
                </div>
                <IconChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function attendeeNames(booking: BookingDetails): string {
  const names = booking.attendees.map(a => a.name.trim()).filter(Boolean)
  return names.length > 0 ? names.join(", ") : "Unnamed attendee"
}

// The same fallback the staff week uses for a host with no name, so an unnamed account is still
// somebody rather than a blank.
function hostNames(booking: BookingDetails): string {
  const names = booking.hosts.map(host => host.name?.trim() || `Staff #${host.id}`)
  if (names.length === 0) return "No host"
  return `${names.length === 1 ? "Host" : "Hosts"}: ${names.join(", ")}`
}

function timeRange(booking: BookingDetails, timeZone: string): string {
  const start = minutesToLabel(zonedDateAndMinutes(booking.start, timeZone).minutes)
  if (!booking.end) return start
  return `${start} – ${minutesToLabel(zonedDateAndMinutes(booking.end, timeZone).minutes)}`
}
